import type { Thread } from "@/entities/thread";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import {
  getThreadFilterTagNames,
  isCatalogFilterTag,
  normalizeCatalogFilterTags,
} from "@/features/catalog/utils/tagFilterGroups";
import { useNgStore } from "@/features/ng-filter/stores/ngStore";

export interface ThreadLinkAttributes {
  target?: "_blank";
  rel?: "noopener noreferrer";
}

export function getThreadLinkAttributes(
  openInNewTab: boolean,
): ThreadLinkAttributes {
  return openInNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {};
}

export function filterNgThreads(
  threads: Thread[] | undefined,
  isThreadHidden: (thread: Thread) => boolean,
  showNgContent: boolean,
): Thread[] {
  if (!threads) return [];
  if (showNgContent) return threads;
  return threads.filter((thread) => !isThreadHidden(thread));
}

export function useNgFilteredThreads(threads: Thread[] | undefined): Thread[] {
  const { isThreadHidden, showNgContent } = useNgStore();
  return filterNgThreads(threads, isThreadHidden, showNgContent);
}

/**
 * スレッド一覧に検索フィルターとNGフィルターを適用
 */
export function useFilteredThreads(threads: Thread[] | undefined): Thread[] {
  const { searchQuery, selectedTag, hiddenTags } = useCatalogStore();
  const { isThreadHidden, showNgContent } = useNgStore();
  return filterCatalogThreads(
    threads,
    searchQuery,
    selectedTag,
    hiddenTags,
    isThreadHidden,
    showNgContent,
  );
}

export function filterCatalogThreads(
  threads: Thread[] | undefined,
  searchQuery: string,
  selectedTag: string | null,
  hiddenTags: string[],
  isThreadHidden: (thread: Thread) => boolean,
  showNgContent: boolean,
): Thread[] {
  const ngFilteredThreads = filterNgThreads(
    threads,
    isThreadHidden,
    showNgContent,
  );
  const hiddenTagNames = new Set(normalizeCatalogFilterTags(hiddenTags));
  const selectedCategory =
    selectedTag !== null && isCatalogFilterTag(selectedTag)
      ? selectedTag
      : null;

  return ngFilteredThreads.filter((thread) => {
    const tags = getThreadFilterTagNames(thread.tags);
    if (tags.some((name) => hiddenTagNames.has(name))) return false;

    if (selectedCategory && !tags.includes(selectedCategory)) {
      return false;
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const body = thread.opPost.body.toLowerCase();
      if (!body.includes(query)) return false;
    }

    return true;
  });
}
